import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const OurTeam = () => {
  const practices = [
    {
      name: 'Onyx Health Advisory',
      tagLine: 'Advancing healthcare through policy insights and innovation.',
      description: 'The Health Policy & Strategy Practice partners with government agencies, managed care organizations, hospitals, and nonprofits to translate policy into actionable insights and strategies. Our team specializes in policy analysis, strategic planning, and cross-sector collaboration, helping clients translate complex policy goals into practical, high-impact solutions that advance healthcare delivery, improve health outcomes, and transform health and safety net programs.',
      learnMoreUrl: '/health-advisory',
      team: [
        {
          name: 'Ashley Gray',
          role: 'Founder & CEO',
          imageUrl: '/assets/img/ashley-headshot.jpeg',
          imagePosition: '50% 10%',
          imageZoom: 'cover',
          bio: 'Ashley brings over a decade of experience in Medicaid policy and health care delivery. She has worked at organizations including the MITRE Corporation and Blue Cross Blue Shield Association, where she supported state and federal clients on complex policy and program implementation challenges. Ashley has also served on the Board of Medical Assistance Services for the Commonwealth of Virginia. She holds a Master of Public Policy from the College of William & Mary and a BA from Vanderbilt University.',
          linkedinUrl: 'https://www.linkedin.com/in/ashley-ah-gray-mpp/',
          resumeUrl: '/assets/03-2025-Ashley-Gray-CV.pdf',
        },
        {
          name: 'Aliza Auces',
          role: 'Director',
          imageUrl: '/assets/img/aliza-headshot.jpeg',
          imagePosition: 'top',
          imageZoom: 'cover',
          bio: 'Aliza is a Medicaid policy expert with extensive experience supporting state agencies and the Centers for Medicare & Medicaid Services (CMS). Her work has focused on program integrity, managed care, and strategic policy development. Previously, Aliza worked at the MITRE Corporation and Blue Cross Blue Shield Association. She holds a Master of Public Health from the Harvard T.H. Chan School of Public Health and a BA from the College of Idaho.',
          linkedinUrl: 'https://www.linkedin.com/in/alizaauces/',
          resumeUrl: '/assets/Aliza-Auces-Resume.pdf',
        },
      ],
    },
    {
      name: 'Onyx Labs',
      tagLine: 'Building intelligent systems with AI, data engineering, and digital innovation.',
      description: 'Onyx Labs partners with technology companies, data-driven enterprises, and mission-focused organizations to design and implement intelligent systems that accelerate innovation and improve performance. Our team specializes in AI/ML strategy, data architecture, software engineering, and digital product development, helping clients transform complex ideas into scalable, real-world solutions.',
      learnMoreUrl: '/onyx-labs',
      team: [
        {
          name: 'Rocky Gray',
          role: 'Head of Onyx Labs, CTO',
          imageUrl: '/assets/img/rocky-headshot-wide.png',
          imagePosition: '50% 50%',
          imageZoom: 'cover',
          bio: 'Rocky is a technology executive with deep expertise in platform engineering, product leadership, and data systems. He has held engineering leadership roles at some of the most influential companies in tech, including Reddit, Twitter, Block, and Capital One — with a career rooted in building high-scale platforms across payments, regulated banking, and card industries that process billions of transactions. He is now at the forefront of helping teams design and ship agentic systems and AI-powered products, translating emerging technology into real business impact. As an entrepreneur and advisor, Rocky has helped organizations navigate complex technical transformations and build the infrastructure to move fast at scale. He specializes in engineering leadership, product strategy, and building high-performing technical teams.',
          linkedinUrl: 'https://www.linkedin.com/in/grocky/',
          resumeUrl: 'https://resume.rockygray.com',
        },
      ],
    },
  ];

  const values = [
    {
      title: 'Meaningful Work',
      description: 'We are driven by the opportunity to make a real difference in healthcare and technology.',
    },
    {
      title: 'Balance',
      description: 'We believe in sustainable growth that respects personal well-being and professional excellence.',
    },
    {
      title: 'Honesty',
      description: 'We build trust through transparency, integrity, and candid communication.',
    },
    {
      title: 'Growth',
      description: 'We are committed to continuous learning, innovation, and development.',
    },
  ];

  return (
    <div>
      <Navigation />

      {/* Hero Section */}
      <section className="hero-section pt-32 pb-16 bg-gradient-to-br from-gray-900 via-onyx-900 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl text-white mb-6">
              Our Team
            </h1>
            <p className="text-xl text-white max-w-5xl mx-auto">
              Onyx Rose Advisors combines deep policy expertise with technical innovation. Our team includes leaders from Onyx Health Advisory and Onyx Labs, working together to deliver strategic, actionable solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Practice Sections */}
      {practices.map((practice, practiceIndex) => (
        <section key={practiceIndex} className={`py-24 ${practiceIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-20 px-6 lg:px-8 xl:grid-cols-3">
            <div className="mx-auto max-w-2xl lg:mx-0">
              <h2 className="text-4xl font-semibold tracking-tight text-accent sm:text-5xl">
                {practice.name}
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                {practice.description}
              </p>
              <Link
                to={practice.learnMoreUrl}
                className="mt-8 inline-block text-accent font-semibold hover:text-accent-700 transition-colors duration-200"
              >
                Learn more →
              </Link>
            </div>
            <ul
              className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-20 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:gap-x-8 xl:col-span-2"
            >
              {practice.team.map((person) => (
                <li key={person.name}>
                  <div className="aspect-[3/2] w-full rounded-2xl overflow-hidden outline-1 -outline-offset-1 outline-black/5">
                      <div
                          role="img"
                          aria-label={person.name}
                          className="w-full h-full bg-gray-100"
                          style={{
                              backgroundImage: `url(${person.imageUrl})`,
                              backgroundPosition: person.imagePosition,
                              backgroundSize: person.imageZoom,
                              backgroundRepeat: 'no-repeat',
                          }}
                      />
                  </div>
                  <h3 className="mt-6 text-lg font-semibold text-gray-900">{person.name}</h3>
                  <p className="text-base text-gray-600">{person.role}</p>
                  <p className="mt-4 text-base text-gray-600 leading-relaxed">{person.bio}</p>
                  <ul className="mt-6 flex gap-x-6">
                    <li>
                      <a href={person.linkedinUrl} className="text-gray-400 hover:text-gray-500">
                        <span className="sr-only">LinkedIn</span>
                        <svg fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" className="w-5 h-5">
                          <path
                            d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                            clipRule="evenodd"
                            fillRule="evenodd"
                          />
                        </svg>
                      </a>
                    </li>
                    <li>
                      <a href={person.resumeUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-500">
                        <span className="sr-only">Resume</span>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </a>
                    </li>
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ))}

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-accent mb-6">
                Our Commitment to Our Team
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center p-6"
              >
                <h3 className="text-2xl font-bold text-black mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-900 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Team Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-accent mb-6">
            Join Our Team
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 leading-relaxed mb-8">
            Are you passionate about making a meaningful impact in healthcare and technology, and advancing your professional growth? We are always looking for talented individuals to join our team.
          </p>
          <p className="text-lg sm:text-xl text-gray-700 leading-relaxed mb-10">
            If you're interested in employment opportunities, contact us and explore how you can contribute to our mission while growing your career. We look forward to hearing from you!
          </p>
          <a
            href="mailto:info@onyxroseadvisors.com?subject=Career%20Opportunities%20at%20Onyx%20Rose%20Advisors&body=I%20would%20like%20to%20learn%20more%20about%20opportunities%20with%20Onyx%20Rose%20Advisors."
            className="inline-block bg-accent text-white font-semibold px-10 py-4 rounded-md hover:bg-accent-900 transition-all duration-200 shadow-lg hover:shadow-xl text-lg"
          >
            Contact Us
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OurTeam;
